import { toast } from "react-toastify";
import React, {
    ChangeEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faXmark,
    faCheck,
    faSpinner,
    faChevronRight,
    faBorderInner,
    faArrowRotateLeft,
} from "@fortawesome/pro-solid-svg-icons";
import {
    faPen,
    faTrash,
    faRulerHorizontal,
} from "@fortawesome/pro-regular-svg-icons";
import { Accordion } from "radix-ui";
import { confirmAlert } from "react-confirm-alert";

import { MeasureData as TransformedToMeasureData } from "@treegram/viewer-core";

import * as THREE from "three";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";
import useModelingToolsContext from "@/services/context/viewer/modeling-tools/use-modeling-tools-context.ts";
import useStoreyMeasuresContext from "../context/measures/use-storey-measures-context.ts";
import useStoreyMeasurementContext from "../context/measurement/use-storey-measurement-context.ts";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";
import useViewerItemsVisibilityContext from "@/services/context/viewer/items-visibility/use-viewer-items-visibility-context.ts";

import { ModelingType } from "@/services/context/viewer/modeling-tools/modeling-tools-context-provider.tsx";
import { Measure3dViewerRef } from "@/pages/takeoff/layouts/measures/layouts/storey-measures/components/measure-3d-viewer-container.tsx";
import MeasureType from "@/pages/takeoff/context/project-measure/models/measure-type.ts";

import Card from "@/components/card";
import Button from "@/components/buttons/button";
import CheckBox from "@/components/checkbox/index.tsx";
import { Case, Switch } from "@/components/switch";
import ProjectEditButton from "@/components/buttons/project-edit-button";
import ToggleButton from "@/components/buttons/button-toggle";
import Tooltip from "@/components/tooltip";

import MeasureDetailsCard from "./measure-details-card.tsx";
import StoreyMeasuresToolbar from "./storey-measures-toolbar.tsx";
import AddMeasureTypeDialog from "./dialogs/add-measure-type-dialog.tsx";
import EditMeasureTypeDialog from "./dialogs/edit-measure-type-dialog.tsx";
import ClosableDialog from "@/components/dialogs/closable-dialog";
import removeDoubleSpaces from "@/utils/text/remove-double-space.ts";
import useStoreyMeasuresDrawingTool from "@/pages/takeoff/layouts/measures/layouts/storey-measures/hooks/use-storey-measures-drawing-tool.ts";
import useStoreyMeasuresEditingTool from "@/pages/takeoff/layouts/measures/layouts/storey-measures/hooks/use-storey-measures-editing-tool.ts";
import MeasureColors from "@/models/enums/measure-colors.ts";

interface MeasureDataProps {
    viewerRef: React.RefObject<Measure3dViewerRef | null>;
    handleSave: () => Promise<boolean | void>;
    getTransformedToMeasureDataFromGeometryId: (
        geometryId: string,
    ) => TransformedToMeasureData | undefined;
}

const MeasureData: React.FC<MeasureDataProps> = ({
    viewerRef,
    handleSave,
    getTransformedToMeasureDataFromGeometryId,
}) => {
    const { ensureCanEdit, canEdit } = useProjectEditAccess();
    const {
        projectMeasureTypes,
        deletedMeasureTypeIds,
        getDrawingTypeFromShapeId,
        getDrawingTypeFromMeasureTypeId,
        getDrawingMeasureRepresentation,
        markMeasureTypeForDeletion,
    } = useProjectMeasureContext();
    const {
        measures,
        isSaving,
        tempMeasures,
        removeMeasure,
        setSelectedMeasures,
    } = useStoreyMeasuresContext();
    const { isInDrawingMode, isInEditingMode, isCurrentlyDrawing } =
        useModelingToolsContext();
    const {
        isMeasurementToolOpen,
        measuredLines,
        closeMeasurementTool,
        clearMeasuredLines,
    } = useStoreyMeasurementContext();
    const { addItems, removeItems } = useViewerItemsVisibilityContext();

    const { startDrawing, stopDrawing } = useStoreyMeasuresDrawingTool();
    const { startEditing, stopEditing } = useStoreyMeasuresEditingTool();

    const [measureTypes, setMeasureTypes] = useState<
        Record<string, MeasureType[]>
    >({});

    const projectMeasureTypeIds = useMemo(
        () => projectMeasureTypes.map((m) => m.measureTypeId),
        [projectMeasureTypes],
    );

    const [openedMeasureGroups, setOpenedMeasureGroups] = useState<string[]>(
        [],
    );

    // A la première arrivée sur la page, tous les types sont cochés.
    const [checkedMeasureTypes, setCheckedMeasureTypes] = useState<string[]>(
        projectMeasureTypeIds,
    );

    const [highlightedMeasureTypeId, setHighlightedMeasureTypeId] = useState<
        string | undefined
    >(undefined);

    const [isAddMeasureTypeDialogOpen, setIsAddMeasureTypeDialogOpen] =
        useState(false);

    const [measureTypeIdToEdit, setMeasureTypeIdToEdit] = useState<
        string | undefined
    >(undefined);
    const [editRepresentationOnly, setEditRepresentationOnly] = useState(false);
    const [isEditMeasureTypeDialogOpen, setIsEditMeasureTypeDialogOpen] =
        useState(true);

    const [isSnappingEnabled, setIsSnappingEnabled] = useState<boolean>(false);

    const measureAddedRef = useRef<string>("");

    const [dontAskForDeletion, setDontAskForDeletion] =
        useState<boolean>(false);

    const setupMeasuresCreation = useCallback(
        async (addedMeasureType: MeasureType) => {
            if (!ensureCanEdit()) {
                return;
            }

            if (isMeasurementToolOpen) {
                closeMeasurementTool();
                await viewerRef.current?.stopMeasurement();
            }

            const { measureTypeId } = addedMeasureType;
            measureAddedRef.current = measureTypeId;

            await startDrawing(
                getDrawingTypeFromShapeId(addedMeasureType.measureShapeId),
                false,
                {
                    measureTypeId: measureTypeId,
                    drawingColor: addedMeasureType.dataInformations?.color
                        ? new THREE.Color(
                              addedMeasureType.dataInformations.color as string,
                          )
                        : new THREE.Color(MeasureColors.Wall),
                    drawingAlpha:
                        typeof addedMeasureType.dataInformations?.alpha ===
                        "number"
                            ? addedMeasureType.dataInformations.alpha
                            : 1,
                    drawingMeasureRepresentation:
                        getDrawingMeasureRepresentation(addedMeasureType),
                    drawingDataInformations: addedMeasureType.dataInformations,
                },
            );
        },
        [
            ensureCanEdit,
            isMeasurementToolOpen,
            startDrawing,
            getDrawingTypeFromShapeId,
            getDrawingMeasureRepresentation,
            closeMeasurementTool,
            viewerRef,
        ],
    );

    const closeMeasuresCreation = useCallback(async () => {
        tempMeasures.forEach((measure) => {
            viewerRef.current?.removeMeasure(measure.measureId);
            removeMeasure(measure.measureId);
        });

        await stopDrawing(true);
        measureAddedRef.current = "";
    }, [tempMeasures, stopDrawing, viewerRef, removeMeasure]);

    const closeMeasuresEdition = useCallback(() => {
        stopEditing(false);
    }, [stopEditing]);

    const saveAndCloseMeasuresCreation = useCallback(async () => {
        if (!ensureCanEdit()) {
            return;
        }

        if (viewerRef.current == null) return;
        try {
            await stopDrawing(false);
            await handleSave();
        } finally {
            measureAddedRef.current = "";
        }
    }, [ensureCanEdit, viewerRef, stopDrawing, handleSave]);

    const saveAndCloseMeasuresEdition = useCallback(async () => {
        if (!ensureCanEdit()) {
            return;
        }

        if (viewerRef.current == null) return;

        try {
            await stopEditing(true);
            await handleSave();
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : "Modification impossible.",
            );
        }
    }, [ensureCanEdit, viewerRef, stopEditing, handleSave]);

    const handleMeasureVisibilityChange = useCallback(
        (measureTypeId: string, visible: boolean) => {
            if (visible) {
                setCheckedMeasureTypes((previous) =>
                    previous.includes(measureTypeId)
                        ? previous
                        : [...previous, measureTypeId],
                );
            } else {
                setCheckedMeasureTypes((previous) =>
                    previous.filter((mt) => mt !== measureTypeId),
                );
            }

            const measuresOfThisType = measures
                .filter((m) => m.measureTypeId === measureTypeId)
                .map((m) => m.measureId)
                .filter((m): m is Extract<typeof m, string> => m != null);

            const filtered = measures.filter(
                (m) =>
                    m.measureId != null &&
                    measuresOfThisType.includes(m.measureId),
            );
            const geometriesIds = filtered.flatMap((m) =>
                m.geometryRepresentations.map((gm) => gm.geometryId),
            );

            if (visible) addItems(geometriesIds);
            else removeItems(geometriesIds);
        },
        [measures, addItems, removeItems],
    );

    const handleMeasureTypeVisibilityCheckChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const { measureTypeId } = e.currentTarget.dataset;

            if (measureTypeId == null) return;

            handleMeasureVisibilityChange(measureTypeId, e.target.checked);
        },
        [handleMeasureVisibilityChange],
    );

    const handleHighLightMeasureType = useCallback(
        (measureTypeId: string) => {
            setHighlightedMeasureTypeId(measureTypeId);

            // Scroll sur l'élément sélectionné avec ouverture de l'accordéon parent si nécessaire
            const parentGroup = Object.entries(measureTypes).find(
                ([, measuresOfThisWord]) =>
                    measuresOfThisWord.some(
                        (mt) => mt.measureTypeId === measureTypeId,
                    ),
            )?.[0];

            if (parentGroup != null) {
                setOpenedMeasureGroups((previous) =>
                    previous.includes(parentGroup)
                        ? previous
                        : [...previous, parentGroup],
                );
            }

            window.setTimeout(() => {
                // Attente de l'ouverture de l'accordéon parent
                const el = document.getElementById(
                    `div-measure-type-${measureTypeId}`,
                );
                if (!el) return;

                el.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            }, 250);

            const filtered = measures.filter(
                (measure) => measure.measureTypeId === measureTypeId,
            );
            const geometriesIds = filtered.flatMap((measure) =>
                measure.geometryRepresentations.map(
                    (geometry) => geometry.geometryId,
                ),
            );

            setSelectedMeasures(
                filtered.length > 0
                    ? {
                          measures: filtered,
                          origin: "list",
                      }
                    : undefined,
            );

            viewerRef.current?.highlightGeometries(geometriesIds, true);
        },
        [measureTypes, measures, setSelectedMeasures, viewerRef],
    );

    const isAllMeasureTypesChecked = projectMeasureTypes.every((mt) =>
        checkedMeasureTypes.includes(mt.measureTypeId),
    );

    const handleAllMeasureTypesVisibilityCheckChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            projectMeasureTypes.forEach((mt) => {
                handleMeasureVisibilityChange(
                    mt.measureTypeId,
                    e.target.checked,
                );
            });
        },
        [projectMeasureTypes, handleMeasureVisibilityChange],
    );

    const handleDeletion = useCallback(
        (measureTypeId: string) => {
            markMeasureTypeForDeletion(measureTypeId);

            // Marquer toutes les mesures de ce type comme supprimées dans le viewer + état local
            const measuresOfThisType = measures.filter(
                (m) => m.measureTypeId === measureTypeId && m.measureId != null,
            );
            measuresOfThisType.forEach((m) => {
                if (m.measureId) {
                    viewerRef.current?.removeMeasure(m.measureId);
                    removeMeasure(m.measureId);
                }
            });
        },
        [markMeasureTypeForDeletion, measures, viewerRef, removeMeasure],
    );

    const handleDeleteMeasureType = useCallback(
        (measureType: MeasureType) => {
            const { measureTypeId, name } = measureType;

            if (dontAskForDeletion) {
                handleDeletion(measureTypeId);
            } else {
                confirmAlert({
                    customUI: ({ onClose }) => (
                        <ClosableDialog
                            open={true}
                            onClose={onClose}
                            title="Suppression de type de mesure"
                        >
                            <div className="flex flex-col gap-y-2 text-sm">
                                <span className="text-red-500">
                                    Êtes-vous sûr de vouloir supprimer tous les
                                    mesures {name} ?
                                </span>

                                <div className="flex flex-row gap-x-2 items-center">
                                    <CheckBox
                                        onChange={(event) =>
                                            setDontAskForDeletion(
                                                event.target.checked,
                                            )
                                        }
                                    />
                                    <span>
                                        Ne plus demander pour cette session
                                    </span>
                                </div>

                                <div className="flex flex-row gap-x-2 ml-auto">
                                    <Button variant="ghost" onClick={onClose}>
                                        Non
                                    </Button>
                                    <Button
                                        variant="danger"
                                        onClick={() => {
                                            onClose();
                                            handleDeletion(measureTypeId);
                                        }}
                                    >
                                        Oui
                                    </Button>
                                </div>
                            </div>
                        </ClosableDialog>
                    ),
                });
            }
        },
        [dontAskForDeletion, handleDeletion],
    );

    const handleMeasureTypeAdded = useCallback(
        (addedMeasureType: MeasureType) => {
            setCheckedMeasureTypes((previous) => [
                ...previous,
                addedMeasureType.measureTypeId,
            ]);

            const parentGroup = addedMeasureType.name.split(" ")[0];

            setOpenedMeasureGroups((previous) =>
                previous.includes(parentGroup)
                    ? previous
                    : [...previous, parentGroup],
            );

            window.setTimeout(() => {
                const el = document.getElementById(
                    `div-measure-type-${addedMeasureType.measureTypeId}`,
                );
                if (el == null) return;

                el.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });

                setupMeasuresCreation(addedMeasureType);
            }, 250);
        },
        [setupMeasuresCreation],
    );

    useEffect(() => {
        const typePerKeyWord = projectMeasureTypes.reduce(
            (acc, mt) => {
                const normalizedName = removeDoubleSpaces(mt.name);
                const word = normalizedName.split(" ")[0];

                acc[word] = acc[word] ?? [];
                acc[word].push(mt);
                return acc;
            },
            {} as Record<string, MeasureType[]>,
        );

        const sortedMeasureTypes = Object.keys(typePerKeyWord)
            .sort((a, b) =>
                a.localeCompare(b, "fr", {
                    sensitivity: "base",
                    numeric: true,
                }),
            )
            .reduce<Record<string, MeasureType[]>>((acc, key) => {
                acc[key] = typePerKeyWord[key].sort((a, b) =>
                    a.name.localeCompare(b.name, "fr", {
                        sensitivity: "base",
                        numeric: true,
                    }),
                );

                return acc;
            }, {});

        setMeasureTypes(sortedMeasureTypes);
    }, [projectMeasureTypes]);

    // GESTION TOUCHES CLAVIER
    const handlersRef = React.useRef({
        isCurrentlyDrawing,
        closeMeasuresCreation,
        saveAndCloseMeasuresCreation,
        closeMeasurementTool,
    });

    useEffect(() => {
        handlersRef.current = {
            isCurrentlyDrawing,
            closeMeasuresCreation,
            saveAndCloseMeasuresCreation,
            closeMeasurementTool,
        };
    }, [
        isCurrentlyDrawing,
        closeMeasuresCreation,
        saveAndCloseMeasuresCreation,
        closeMeasurementTool,
    ]);

    useEffect(() => {
        if (!isInDrawingMode && !isInEditingMode && !isMeasurementToolOpen)
            return undefined;

        const keyEvents = (e: KeyboardEvent) => {
            const handlers = handlersRef.current;
            switch (e.key) {
                case "Escape":
                    if (isInDrawingMode && !handlers.isCurrentlyDrawing())
                        handlers.closeMeasuresCreation();
                    if (isInEditingMode) {
                        closeMeasuresEdition();
                    }
                    if (
                        isMeasurementToolOpen &&
                        !handlers.isCurrentlyDrawing()
                    ) {
                        handlers.closeMeasurementTool();
                        viewerRef.current?.stopMeasurement();
                    }
                    break;
                case "Enter":
                    if (isInDrawingMode && !handlers.isCurrentlyDrawing())
                        handlers.saveAndCloseMeasuresCreation();
                    if (isInEditingMode) saveAndCloseMeasuresEdition();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener("keydown", keyEvents, { capture: true });

        return () => {
            window.removeEventListener("keydown", keyEvents, { capture: true });
        };
    }, [
        isInDrawingMode,
        isInEditingMode,
        isMeasurementToolOpen,
        closeMeasuresEdition,
        saveAndCloseMeasuresEdition,
        viewerRef,
    ]);

    return (
        <>
            <Tooltip id="measure-data-card-tooltip" className="z-12" />

            <div className="absolute flex flex-row gap-x-4 inset-4 z-11 pointer-events-none">
                <div className="flex flex-col gap-y-2 justify-between min-w-80 w-fit">
                    <Card
                        additionalClasses="overflow-hidden h-fit"
                        additionalContentClasses="flex flex-col min-h-0"
                    >
                        <div className="border-b border-slate-300 flex py-2 px-4">
                            <div className="text-sm font-semibold text-gray-800 uppercase flex-1">
                                Métrés
                            </div>
                        </div>

                        <div className="flex flex-col gap-y-0.5 overflow-y-auto pl-2 my-1.5 pr-4 text-sm">
                            <div className="flex flex-row items-center gap-x-2 ml-4.5">
                                <CheckBox
                                    checked={isAllMeasureTypesChecked}
                                    disabled={
                                        isInDrawingMode || isInEditingMode
                                    }
                                    onChange={
                                        handleAllMeasureTypesVisibilityCheckChange
                                    }
                                />
                                <span>Tout afficher</span>
                            </div>

                            <Accordion.Root
                                type="multiple"
                                value={openedMeasureGroups}
                                onValueChange={setOpenedMeasureGroups}
                            >
                                {Object.entries(measureTypes).map(
                                    ([word, measuresOfThisWord]) => (
                                        <Accordion.Item value={word} key={word}>
                                            <Accordion.Header>
                                                <div className="flex flex-row items-center gap-x-1">
                                                    <Accordion.Trigger className="group">
                                                        <FontAwesomeIcon
                                                            icon={
                                                                faChevronRight
                                                            }
                                                            className="transition-transform duration-300 ease-in-out group-data-[state=open]:rotate-90 text-xs"
                                                        />
                                                    </Accordion.Trigger>
                                                    <CheckBox
                                                        disabled={
                                                            isInDrawingMode ||
                                                            isInEditingMode
                                                        }
                                                        onChange={(e) => {
                                                            measuresOfThisWord.forEach(
                                                                (mt) => {
                                                                    handleMeasureVisibilityChange(
                                                                        mt.measureTypeId,
                                                                        e.target
                                                                            .checked,
                                                                    );
                                                                },
                                                            );
                                                        }}
                                                        checked={measuresOfThisWord.every(
                                                            (mt) =>
                                                                checkedMeasureTypes.find(
                                                                    (ct) =>
                                                                        ct ===
                                                                        mt.measureTypeId,
                                                                ),
                                                        )}
                                                    />

                                                    <Accordion.Trigger>
                                                        <span className="select-none">
                                                            {word}
                                                        </span>
                                                    </Accordion.Trigger>

                                                    <span className="ml-auto text-xs text-gray-600">
                                                        ( x{" "}
                                                        {measuresOfThisWord
                                                            .flatMap(
                                                                (mt) =>
                                                                    measures.filter(
                                                                        (m) =>
                                                                            m.measureTypeId ===
                                                                            mt.measureTypeId,
                                                                    ).length -
                                                                    tempMeasures.filter(
                                                                        (m) =>
                                                                            m.measureTypeId ===
                                                                            mt.measureTypeId,
                                                                    ).length,
                                                            )
                                                            .reduce(
                                                                (a, b) => a + b,
                                                            )}{" "}
                                                        )
                                                    </span>
                                                </div>
                                            </Accordion.Header>

                                            <Accordion.Content className="overflow-hidden data-[state=open]:animate-slideDown data-[state=closed]:animate-slideUp">
                                                <div className="flex flex-col gap-y-1 ml-8 my-0.75">
                                                    {measuresOfThisWord
                                                        .sort((a, b) =>
                                                            removeDoubleSpaces(
                                                                a.name,
                                                            ).localeCompare(
                                                                removeDoubleSpaces(
                                                                    b.name,
                                                                ),
                                                                "fr",
                                                                {
                                                                    sensitivity:
                                                                        "base",
                                                                    numeric: true,
                                                                },
                                                            ),
                                                        )
                                                        .map((mt) => {
                                                            const measuresOfThisType =
                                                                measures.filter(
                                                                    (m) =>
                                                                        m.measureTypeId ===
                                                                        mt.measureTypeId,
                                                                );
                                                            const quantity =
                                                                measuresOfThisType.length;
                                                            const toDelete =
                                                                deletedMeasureTypeIds.includes(
                                                                    mt.measureTypeId,
                                                                );
                                                            return (
                                                                <div
                                                                    className="grid grid-cols-[auto_auto_1fr_auto] flex-1 gap-x-1 items-center"
                                                                    key={
                                                                        mt.measureTypeId
                                                                    }
                                                                >
                                                                    <CheckBox
                                                                        disabled={
                                                                            isInDrawingMode ||
                                                                            isInEditingMode
                                                                        }
                                                                        checked={checkedMeasureTypes.includes(
                                                                            mt.measureTypeId,
                                                                        )}
                                                                        onChange={
                                                                            handleMeasureTypeVisibilityCheckChange
                                                                        }
                                                                        data-measure-type-id={
                                                                            mt.measureTypeId
                                                                        }
                                                                    />

                                                                    <ProjectEditButton
                                                                        variant="ghost"
                                                                        size="3xs"
                                                                        className="size-6 justify-center"
                                                                        disabled={
                                                                            isInDrawingMode ||
                                                                            isInEditingMode ||
                                                                            toDelete
                                                                        }
                                                                        aria-label={`Modifier la représentation du type ${mt.name}`}
                                                                        data-tooltip-id="measure-data-card-tooltip"
                                                                        data-tooltip-content="Modifier la représentation du type"
                                                                        onClick={(
                                                                            event,
                                                                        ) => {
                                                                            event.stopPropagation();
                                                                            setEditRepresentationOnly(
                                                                                true,
                                                                            );
                                                                            setMeasureTypeIdToEdit(
                                                                                mt.measureTypeId,
                                                                            );
                                                                            setIsEditMeasureTypeDialogOpen(
                                                                                true,
                                                                            );
                                                                        }}
                                                                    >
                                                                        <span
                                                                            className="size-4 rounded-sm border border-gray-400"
                                                                            style={{
                                                                                backgroundColor:
                                                                                    String(
                                                                                        mt
                                                                                            .dataInformations
                                                                                            ?.color ??
                                                                                            "#3B82F6",
                                                                                    ),
                                                                            }}
                                                                        />
                                                                    </ProjectEditButton>

                                                                    <ToggleButton
                                                                        id={`div-measure-type-${mt.measureTypeId}`}
                                                                        variant="ghost"
                                                                        size="2xs"
                                                                        disabled={
                                                                            quantity ===
                                                                                0 ||
                                                                            isInDrawingMode ||
                                                                            isInEditingMode
                                                                        }
                                                                        setIsToggled={() =>
                                                                            handleHighLightMeasureType(
                                                                                mt.measureTypeId,
                                                                            )
                                                                        }
                                                                        toggled={
                                                                            highlightedMeasureTypeId ===
                                                                            mt.measureTypeId
                                                                        }
                                                                        redoToggleAction={
                                                                            true
                                                                        }
                                                                    >
                                                                        <span className="text-gray-900">
                                                                            {
                                                                                mt.name
                                                                            }
                                                                        </span>
                                                                    </ToggleButton>

                                                                    <div className="flex flex-row gap-x-px items-center">
                                                                        <ProjectEditButton
                                                                            variant="ghost"
                                                                            size="3xs"
                                                                            disabled={
                                                                                isInDrawingMode ||
                                                                                isInEditingMode ||
                                                                                toDelete
                                                                            }
                                                                            data-tooltip-id="measure-data-card-tooltip"
                                                                            data-tooltip-content="Editer ce type de métré"
                                                                            onClick={() => {
                                                                                setEditRepresentationOnly(
                                                                                    false,
                                                                                );
                                                                                setMeasureTypeIdToEdit(
                                                                                    mt.measureTypeId,
                                                                                );
                                                                                setIsEditMeasureTypeDialogOpen(
                                                                                    true,
                                                                                );
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={
                                                                                    faPen
                                                                                }
                                                                            />
                                                                        </ProjectEditButton>

                                                                        <span className="ml-auto text-xs text-gray-600 pr-2">
                                                                            ( x{" "}
                                                                            {quantity -
                                                                                tempMeasures.filter(
                                                                                    (
                                                                                        m,
                                                                                    ) =>
                                                                                        m.measureTypeId ===
                                                                                        mt.measureTypeId,
                                                                                )
                                                                                    .length}{" "}
                                                                            )
                                                                        </span>

                                                                        <ProjectEditButton
                                                                            variant="ghost"
                                                                            size="3xs"
                                                                            disabled={
                                                                                isInDrawingMode ||
                                                                                isInEditingMode ||
                                                                                toDelete
                                                                            }
                                                                            data-tooltip-id="measure-data-card-tooltip"
                                                                            data-tooltip-content="Ajouter un métré de ce type"
                                                                            onClick={() => {
                                                                                setupMeasuresCreation(
                                                                                    mt,
                                                                                );
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={
                                                                                    faPlus
                                                                                }
                                                                                className="text-green-500"
                                                                            />
                                                                        </ProjectEditButton>

                                                                        <ProjectEditButton
                                                                            variant="ghost"
                                                                            size="3xs"
                                                                            disabled={
                                                                                isInDrawingMode ||
                                                                                isInEditingMode ||
                                                                                toDelete
                                                                            }
                                                                            data-tooltip-id="measure-data-card-tooltip"
                                                                            data-tooltip-content="Supprimer ce type de métré et les métrés associés"
                                                                            onClick={() =>
                                                                                handleDeleteMeasureType(
                                                                                    mt,
                                                                                )
                                                                            }
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={
                                                                                    faTrash
                                                                                }
                                                                                className="text-red-500"
                                                                            />
                                                                        </ProjectEditButton>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            </Accordion.Content>
                                        </Accordion.Item>
                                    ),
                                )}
                            </Accordion.Root>
                        </div>

                        <ProjectEditButton
                            variant="simple"
                            size="2xs"
                            className="shrink-0 ms-5.5 mb-2 w-fit"
                            onClick={() => setIsAddMeasureTypeDialogOpen(true)}
                            disabled={
                                isSaving || isInDrawingMode || isInEditingMode
                            }
                            data-tooltip-id="measure-data-card-tooltip"
                            data-tooltip-content="Ajouter un type de métré"
                        >
                            <div className="flex flex-row items-center gap-x-2">
                                <FontAwesomeIcon
                                    icon={faPlus}
                                    className="text-green-500"
                                />
                                <span className="text-xs text-gray-700">
                                    Ajouter un type de métré
                                </span>
                            </div>
                        </ProjectEditButton>
                    </Card>

                    <MeasureDetailsCard
                        viewerRef={viewerRef}
                        setMeasureTypeIdToEdit={(measureTypeId: string) => {
                            setEditRepresentationOnly(false);
                            setMeasureTypeIdToEdit(measureTypeId);
                            setIsEditMeasureTypeDialogOpen(true);
                        }}
                        handleSelectMeasureType={handleHighLightMeasureType}
                        handleSave={handleSave}
                        getTransformedToMeasureDataFromGeometryId={
                            getTransformedToMeasureDataFromGeometryId
                        }
                        startDrawing={setupMeasuresCreation}
                        startEditing={startEditing}
                    />
                </div>

                <StoreyMeasuresToolbar
                    viewerRef={viewerRef}
                    closeMeasuresCreation={closeMeasuresCreation}
                />
            </div>

            {isInDrawingMode && (
                <div className="absolute bottom-4 z-11 w-full">
                    <Card
                        colors="bg-green-100 border-green-300 shadow-emerald-200/30"
                        additionalClasses="w-fit mx-auto"
                        additionalContentClasses="flex flex-row gap-x-4 items-center py-2 px-4"
                    >
                        <div className="flex flex-row gap-x-1 items-center text-sm flex-1 font-semibold">
                            <span>
                                Nouveau '
                                {
                                    projectMeasureTypes.find(
                                        (mt) =>
                                            mt.measureTypeId ===
                                            measureAddedRef.current,
                                    )?.name
                                }
                                '
                            </span>
                            <span>
                                (
                                {tempMeasures.filter(
                                    (m) =>
                                        m.measureTypeId ===
                                        measureAddedRef.current,
                                )?.length ?? 0}
                                )
                            </span>
                        </div>

                        <span className="text-gray-400 text-xs">
                            <Switch
                                value={getDrawingTypeFromMeasureTypeId(
                                    measureAddedRef.current,
                                )}
                            >
                                <Case value={ModelingType.Point}>
                                    Saisir dans la 3D
                                </Case>
                                <Case value={ModelingType.Line}>
                                    Saisir dans la 3D
                                </Case>
                                <Case value={ModelingType.Polyline}>
                                    Appuyer sur Entrée pour créer une polyligne
                                </Case>
                                <Case value={ModelingType.Surface}>
                                    Appuyer sur Entrée pour créer une surface
                                </Case>
                                <Case default>Saisir dans la 3D</Case>
                            </Switch>
                        </span>

                        <div className="flex flex-row gap-x-0.5">
                            <ToggleButton
                                toggled={isSnappingEnabled}
                                setIsToggled={(state) => {
                                    setIsSnappingEnabled(state);
                                    viewerRef.current?.setSnappingEnabled(
                                        state,
                                    );
                                }}
                                variant="ghost"
                                size="xs"
                                data-tooltip-id="measure-data-card-tooltip"
                                data-tooltip-content="Activer / Désactiver l'accroche objet"
                            >
                                <FontAwesomeIcon
                                    icon={faBorderInner}
                                    className="text-neutral-800 text-base"
                                />
                            </ToggleButton>

                            <Button
                                variant="ghost"
                                onClick={closeMeasuresCreation}
                                size="xs"
                                disabled={isSaving}
                                data-tooltip-id="measure-data-card-tooltip"
                                data-tooltip-content="Annuler la création"
                            >
                                <FontAwesomeIcon
                                    icon={faXmark}
                                    className="text-red-500 text-base"
                                />
                            </Button>

                            <Button
                                variant="ghost"
                                onClick={saveAndCloseMeasuresCreation}
                                size="xs"
                                disabled={!canEdit || (isSaving)}
                                data-tooltip-id="measure-data-card-tooltip"
                                data-tooltip-content="Enregistrer la création"
                            >
                                <FontAwesomeIcon
                                    icon={isSaving ? faSpinner : faCheck}
                                    spin={isSaving}
                                    className="text-green-500 text-base"
                                />
                            </Button>
                        </div>
                    </Card>
                </div>
            )}

            {isInEditingMode && (
                <div className="absolute bottom-4 z-11 w-full">
                    <Card
                        colors="bg-green-100 border-green-300 shadow-emerald-200/30"
                        additionalClasses="w-fit mx-auto"
                        additionalContentClasses="flex flex-row gap-x-4 items-center py-2 px-4"
                    >
                        <div className="text-sm flex-1 font-semibold">
                            <span>Edition en cours</span>
                        </div>

                        <div className="flex flex-row gap-x-0.5">
                            <ToggleButton
                                toggled={isSnappingEnabled}
                                setIsToggled={(state) => {
                                    setIsSnappingEnabled(state);
                                    viewerRef.current?.setSnappingEnabled(
                                        state,
                                    );
                                }}
                                variant="ghost"
                                size="xs"
                                data-tooltip-id="measure-data-card-tooltip"
                                data-tooltip-content="Activer / Désactiver l'accroche objet"
                            >
                                <FontAwesomeIcon
                                    icon={faBorderInner}
                                    className="text-neutral-800 text-base"
                                />
                            </ToggleButton>

                            <Button
                                variant="ghost"
                                onClick={closeMeasuresEdition}
                                size="xs"
                                disabled={isSaving}
                                data-tooltip-id="measure-data-card-tooltip"
                                data-tooltip-content="Annuler l’édition"
                            >
                                <FontAwesomeIcon
                                    icon={faXmark}
                                    className="text-red-500 text-base"
                                />
                            </Button>

                            <Button
                                variant="ghost"
                                onClick={saveAndCloseMeasuresEdition}
                                size="xs"
                                disabled={!canEdit || (isSaving)}
                                data-tooltip-id="measure-data-card-tooltip"
                                data-tooltip-content="Enregistrer l’édition"
                            >
                                <FontAwesomeIcon
                                    icon={isSaving ? faSpinner : faCheck}
                                    spin={isSaving}
                                    className="text-green-500 text-base"
                                />
                            </Button>
                        </div>
                    </Card>
                </div>
            )}

            {isMeasurementToolOpen && (
                <>
                    <div className="absolute bottom-4 z-11 w-full flex flex-row gap-x-4 items-end justify-center">
                        <Card
                            colors="bg-green-100 border-green-300 shadow-emerald-200/30"
                            additionalClasses="w-fit "
                            additionalContentClasses="flex flex-row gap-x-4 items-center py-2 px-4"
                        >
                            <div className="text-sm flex-1 font-semibold flex flex-row items-center gap-x-2">
                                <FontAwesomeIcon icon={faRulerHorizontal} />
                                <span>Mesure</span>
                            </div>

                            <span className="text-gray-400 text-xs">
                                Saisir dans la 3D
                            </span>

                            <div className="flex flex-row gap-x-0.5">
                                <ToggleButton
                                    toggled={isSnappingEnabled}
                                    setIsToggled={(state) => {
                                        setIsSnappingEnabled(state);
                                        viewerRef.current?.setSnappingEnabled(
                                            state,
                                        );
                                    }}
                                    variant="ghost"
                                    size="xs"
                                    data-tooltip-id="measure-data-card-tooltip"
                                    data-tooltip-content="Activer / Désactiver l'accroche objet"
                                >
                                    <FontAwesomeIcon
                                        icon={faBorderInner}
                                        className="text-neutral-800 text-base"
                                    />
                                </ToggleButton>

                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => {
                                        clearMeasuredLines();
                                        viewerRef.current?.clearMeasuredLines();
                                    }}
                                    data-tooltip-id="measure-data-card-tooltip"
                                    data-tooltip-content="Nouvelle mesure"
                                >
                                    <FontAwesomeIcon
                                        icon={faArrowRotateLeft}
                                        className="text-green-500 text-base"
                                    />
                                </Button>

                                <Button
                                    variant="ghost"
                                    onClick={() => {
                                        closeMeasurementTool();
                                        viewerRef.current?.stopMeasurement();
                                    }}
                                    size="xs"
                                    data-tooltip-id="measure-data-card-tooltip"
                                    data-tooltip-content="Fermer l'outil de mesure"
                                >
                                    <FontAwesomeIcon
                                        icon={faXmark}
                                        className="text-red-500 text-base"
                                    />
                                </Button>
                            </div>
                        </Card>

                        {measuredLines.length > 0 && (
                            <Card
                                colors="bg-green-100 border-green-300 shadow-emerald-200/30"
                                additionalContentClasses="flex flex-col gap-y-1.5 py-1.5 px-1"
                            >
                                <div className="flex flex-col gap-y-0.5">
                                    {measuredLines.map(
                                        (measurementValue, i) => (
                                            <div
                                                key={i}
                                                className="px-2 text-xs text-right"
                                            >
                                                {(
                                                    Math.round(
                                                        measurementValue * 100,
                                                    ) / 100
                                                ).toFixed(2)}{" "}
                                                m
                                            </div>
                                        ),
                                    )}
                                </div>

                                {measuredLines.length > 1 && (
                                    <div className="text-gray-800 text-xs ml-auto pr-2 border-t border-gray-600 pt-0.5 font-bold">
                                        {(
                                            Math.round(
                                                measuredLines.reduce(
                                                    (prev, current) =>
                                                        prev + current,
                                                    0,
                                                ) * 100,
                                            ) / 100
                                        ).toFixed(2)}{" "}
                                        m
                                    </div>
                                )}
                            </Card>
                        )}
                    </div>
                </>
            )}

            <AddMeasureTypeDialog
                open={isAddMeasureTypeDialogOpen}
                onClose={setIsAddMeasureTypeDialogOpen}
                measureTypeAddedCallback={(type) =>
                    handleMeasureTypeAdded(type)
                }
            />
            {measureTypeIdToEdit && isEditMeasureTypeDialogOpen && (
                <EditMeasureTypeDialog
                    representationOnly={editRepresentationOnly}
                    open={isEditMeasureTypeDialogOpen}
                    onClose={() => {
                        setIsEditMeasureTypeDialogOpen(false);
                        setMeasureTypeIdToEdit(undefined);
                    }}
                    measureTypeId={measureTypeIdToEdit}
                    handleSave={handleSave}
                />
            )}
        </>
    );
};

export default MeasureData;
